import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useMySalonAccessQuery } from '../services/accessControlApi';
import { canSeeDomain, getSalonAccess, hasSalonPermission } from '../utils/salonPermissions';

const SalonAccessContext = createContext(null);
const STORAGE_KEY = 'glow_selected_salon_id';
const PROFESSIONAL_ROLES = new Set([
  'professional_responsable',
  'professional_employe',
  'professional_manager',
  'professional_manager_employee',
]);

const readStoredSalonId = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? Number(stored) : null;
};

export const SalonAccessProvider = ({ children }) => {
  const { user } = useAuth();
  const isProfessional = PROFESSIONAL_ROLES.has(user?.role);
  const query = useMySalonAccessQuery(undefined, { skip: !isProfessional });
  const access = query.data || null;
  const salons = access?.salons || [];
  const [selectedSalonId, setSelectedSalonId] = useState(readStoredSalonId);

  useEffect(() => {
    if (!access) return;
    const selectedIsValid = salons.some(
      (salon) => Number(salon.salon_id) === Number(selectedSalonId),
    );
    const nextId = selectedIsValid ? Number(selectedSalonId) : (salons[0]?.salon_id ?? null);
    setSelectedSalonId(nextId);
    if (nextId == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(nextId));
  }, [access, salons, selectedSalonId]);

  const selectSalon = useCallback((salonId) => {
    const exists = salons.some((salon) => Number(salon.salon_id) === Number(salonId));
    const nextId = exists ? Number(salonId) : (salons[0]?.salon_id ?? null);
    setSelectedSalonId(nextId);
    if (nextId == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(nextId));
  }, [salons]);

  const value = useMemo(() => ({
    access,
    salons,
    selectedSalonId,
    selectedSalon: getSalonAccess(access, selectedSalonId) || null,
    selectSalon,
    hasPermission: (salonId, code) => hasSalonPermission(access, salonId, code),
    canSee: (code) => canSeeDomain(access, code),
    isLoading: isProfessional && query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNoAuthorizedSalon: isProfessional && !query.isLoading && Boolean(access) && salons.length === 0,
  }), [access, salons, selectedSalonId, selectSalon, isProfessional, query.isLoading, query.isError, query.error]);

  return <SalonAccessContext.Provider value={value}>{children}</SalonAccessContext.Provider>;
};

export const useSalonAccess = () => {
  const value = useContext(SalonAccessContext);
  if (!value) throw new Error('useSalonAccess must be used inside SalonAccessProvider');
  return value;
};
